import React from "react";
import gettext from "../../../shared/utils/gettext";
import cropr from "../../../shared/utils/cropr";
import { interpolate } from "../../../shared/utils/legacy";
import DownloadButton from "./components/DownloadButton";

type FarmPlansExportViewProps = {
  cropFieldsCountPerPlan: [number, number][]; // planId, count
  parcelsCount: number;
};

function FarmPlansExportView({ cropFieldsCountPerPlan, parcelsCount }: FarmPlansExportViewProps) {
  const PlansDownloadOptions = [
    {
      key: "shape_file_plan_download",
      href: "/integration/extern/export/fieldplan/%s",
      icon: "far fa-draw-square",
      name: gettext("Shape file"),
      hidden: !cropr.features.export_shape,
    },
    {
      key: "kml_plan_download",
      href: "/integration/extern/export/fieldplan/%s?format=kml",
      icon: "far fa-globe",
      name: `KML (${gettext("Google Earth")})`,
      hidden: !cropr.features.export_shape,
    },
    {
      key: "excel_plan_download",
      href: "/cropr/export/overview_plan_xls/%s",
      icon: "far fa-file-excel",
      name: gettext("Excel sheet"),
      hidden: !cropr.features.export_shape,
    },
    {
      key: "no_subscription_option",
      extraLinkClasses: "",
      onClick: () => {
        // todo fix track_event
        // new TrackEvent("Click", "Report_Cropfield_plan", "NotSubscribed");
      },
      href: "/subscription/my_subscriptions",
      icon: "far fa-th",
      name: gettext("Upgrade"),
      hidden: cropr.features.export_shape,
    },
    {
      key: "cloudfarm_cropfields_plan_download",
      href: "/integration/isobus/taskdata/%s",
      icon: "far fa-tachometer-alt",
      name: `ISO-XML ${gettext("cropfields")} 3.3`,
      hidden: !(cropr.features.export_shape && cropr.features.cloudfarm),
    },
    {
      key: "cloudfarm_planning_plan_download",
      href: "/integration/isobus/taskdata/%s?planning=True",
      icon: "far fa-tachometer",
      name: `ISO-XML ${gettext("planning")} 3.3`,
      hidden: !(cropr.features.export_shape && cropr.features.cloudfarm),
    },
    {
      key: "edi_cropping_scheme_plan_download",
      href: "/integration/edi/edi_croppingscheme_4_02/plan/%s/?download_as_attachment=true",
      icon: "fab fa-xing",
      name: gettext("EDI cropping scheme"),
      hidden: !(cropr.features.export_shape && cropr.features.edi_export),
    },
    {
      key: "edi_crop_recording_plan_download",
      href: "/integration/edi/edi_croprecording_4_02/plan/%s/?download_as_attachment=true",
      icon: "fab fa-xing",
      name: gettext("EDI crop recording"),
      hidden: !(cropr.features.export_shape && cropr.features.edi_export),
    },
  ];

  const ParcelsDownloadOptions = [
    {
      key: "shape_file_parcel_download",
      href: "/integration/extern/export/parcels/",
      icon: "fa-draw-square",
      name: "Shape file",
      hidden: false,
    },
    {
      key: "kml_parcel_download",
      href: "/integration/extern/export/parcels/?format=kml",
      icon: "fa-globe",
      name: "KML (Google Earth)",
      hidden: false,
    },
  ];

  return (
    <div id="export_fieldplan" className="p-4">
      <h3 className="mb-3">{gettext("Field plans")}</h3>
      <p>
        {gettext(
          "Welcome to the Dacom export module. This module allows you to export field plans to various formats (shape file, KML, Excel and Edi Message)."
        )}
      </p>

      {!cropr.features.export_shape && (
        <p className="alert alert-info">
          {gettext("To download reports, a Dacom subscription is required.")}
          <a
            href="/subscription/order/"
            className="btn btn-success btn-large"
            title={gettext("Upgrade account")}
          >
            <i className="far fa-fw mr-2 fa-shopping-cart" />
            {gettext("Upgrade your Dacom account")}
          </a>
        </p>
      )}
      {cropr.plans.length && (
        <div>
          <p>{gettext("You have defined the following plans:")}</p>
          <table className="table table-striped">
            <thead>
              <tr>
                <th className="align-middle pl-0">{gettext("name")}</th>
                <th className="align-middle">{gettext("start")}</th>
                <th className="align-middle">{gettext("end")}</th>
                <th className="align-middle">{gettext("cropfields")}</th>
                <th className="align-middle" style={{ width: "200px" }}>
                  {gettext("downloads")}
                </th>
              </tr>
            </thead>
            <tbody>
              {cropFieldsCountPerPlan.map(([planId, cropFieldsCount]) => {
                const plan = cropr.plans.get(planId);
                return (
                  <tr key={planId}>
                    <td className="align-middle pl-0">{plan.get("name")}</td>
                    <td className="align-middle">{plan.get("period_start")}</td>
                    <td className="align-middle">{plan.get("period_end")}</td>
                    <td className="align-middle">{cropFieldsCount}</td>
                    <td className="align-middle">
                      {cropFieldsCount ? (
                        <DownloadButton key={"downloadBtn-" + planId}>
                          {PlansDownloadOptions.map(
                            (downloadOptionItem) =>
                              !downloadOptionItem.hidden && (
                                <li key={downloadOptionItem.key}>
                                  <a
                                    className={`dropdown-item text-decoration-none ${downloadOptionItem.extraLinkClasses}`}
                                    href={
                                      downloadOptionItem.href.includes("%s")
                                        ? interpolate(downloadOptionItem.href, [planId])
                                        : downloadOptionItem.href
                                    }
                                    onClick={downloadOptionItem.onClick}
                                  >
                                    <i className={`fa-fw mr-2 ${downloadOptionItem.icon}`} />{" "}
                                    {downloadOptionItem.name}
                                  </a>
                                </li>
                              )
                          )}
                        </DownloadButton>
                      ) : (
                        <div>{gettext("No cropfields found")}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {parcelsCount === 0 ? (
        ""
      ) : (
        <div>
          <p>{gettext("You can also export the following parcels")}:</p>
          <table className="table table-striped">
            <thead>
              <tr>
                <th className="align-middle pl-0">{gettext("name")}</th>
                <th className="align-middle">{gettext("parcels")}</th>
                <th className="align-middle" style={{ width: "200px" }}>
                  {gettext("downloads")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="align-middle pl-0">{gettext("All parcels")}</td>
                <td className="align-middle">{parcelsCount}</td>
                <td className="align-middle">
                  <DownloadButton>
                    {ParcelsDownloadOptions.map(
                      (downloadOptionItem) =>
                        !downloadOptionItem.hidden && (
                          <li key={downloadOptionItem.key}>
                            <a
                              className="dropdown-item text-decoration-none"
                              href={downloadOptionItem.href}
                            >
                              <i className={`far fa-fw mr-2 ${downloadOptionItem.icon}`} />{" "}
                              {downloadOptionItem.name}
                            </a>
                          </li>
                        )
                    )}
                  </DownloadButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FarmPlansExportView;
